import useLang from 'lang';
import { formatLocaleDate, formatLocaleDateTime } from 'lang/date-time';

export const STATUS_KEYS: Record<string, string> = {
  created: 'Awaiting approval',
  accepted: 'Accepted',
  fulfilled: 'Fulfilled',
  cancelled: 'Cancelled',
  'waiting-for-payment': 'Waiting for Payment',
  'payment-failed': 'Payment failed',
  'payment-review-required': 'Payment requires review',
};

type PeriodicBooking = {
  frequency: 'daily' | 'weekly' | 'monthly';
  startsOn: string;
  endsOn: string;
};

/**
 * Consistent lifecycle badge used throughout the account hub.
 */
export function ReservationStatusBadge({ status }: { status: string }) {
  const { _t } = useLang();
  return <span className={`account-status account-status--${status}`}>{_t(STATUS_KEYS[status] || status)}</span>;
}

/**
 * Human-readable localized booking interval.
 */
export function BookingInterval({ startsAt, endsAt }: { startsAt: string; endsAt: string }) {
  const { lang } = useLang();
  return (
    <span>
      {formatLocaleDateTime(startsAt, lang)} – {formatLocaleDateTime(endsAt, lang)}
    </span>
  );
}

/**
 * Read-only recurrence marker and bounded series summary.
 */
export function PeriodicBookingBadge({ periodic }: { periodic?: PeriodicBooking | null }) {
  const { _t, lang } = useLang();
  if (!periodic) return null;
  const frequency =
    periodic.frequency === 'daily' ? _t('Daily') : periodic.frequency === 'weekly' ? _t('Weekly') : _t('Monthly');
  return (
    <p className="account-periodic-summary">
      <span className="account-periodic-badge">{_t('Periodic booking')}</span>
      {frequency} · {formatLocaleDate(periodic.startsOn, lang, { dateStyle: 'medium' })} –{' '}
      {formatLocaleDate(periodic.endsOn, lang, { dateStyle: 'medium' })}
    </p>
  );
}

/**
 * Triggers a browser save/open flow for a server-produced calendar blob.
 */
export function openCalendarBlob(blob: Blob, reservationId: number) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `reservation-${reservationId}.ics`;
  anchor.click();
  URL.revokeObjectURL(url);
}

/**
 * Starts a fresh booking flow without restoring an abandoned slot selection.
 */
export function clearBookingDraft() {
  sessionStorage.removeItem('bookingState');
}

/**
 * Shared retryable error state.
 */
export function AccountError({ retry }: { retry: () => void }) {
  const { _t } = useLang();
  return (
    <div className="account-state account-state--error">
      <p>{_t('We could not load this information.')}</p>
      <button className="account-button account-button--secondary" onClick={retry}>
        {_t('Try again')}
      </button>
    </div>
  );
}

/**
 * Shared skeleton while account data is loading.
 */
export function AccountSkeleton() {
  return (
    <div className="account-skeleton" aria-busy="true">
      <span />
      <span />
      <span />
    </div>
  );
}
